import { useLinkSigner } from '../../store/link/hooks'
import { useChangePubKeyChainId } from '../../store/app/hooks'
import { store, useAppDispatch } from '../../store'
import { useCallback } from 'react'
import { updateAccountActivatingAction } from '../../store/account/actions'
import { Equal } from '../../utils/equal'
import {
  AccountInfo,
  ActivateStatus,
  getAccountNonce,
  getFee,
} from '../../api/v1/account'
import { signChangePubKey } from '../../utils/signer/transactions/changePubKey'
import { SUB_ACCOUNT_ID } from '../../config'
import { e2w, w2e } from '../../utils/number'
import {
  postChangePubKey,
  PostChangePubKeyBody,
} from '../../api/v1/changePubKey'
import { updateAccountLockState } from '../../store/link/actions'
import toastify from '../../components/Toastify'
import { updateModal } from '../../store/app/actions/modalActions'
import { sleep } from '../../utils/sleep'
import { getAccountInfoAction } from '../../store/account/actions/getAccountInfoActions'
import { NetworkRegistry } from '../../utils/networkRegistry'
import { UserRejectedError } from '../../utils/errors/userReject'
import { useAtomValue } from 'jotai'
import { referralCodeAtom } from '../../views/Rewards/Refer/ReferralsInput/atoms'
import { isValidReferralCode } from '../../views/Rewards/utils'

export function useActivateAccount() {
  const signer = useLinkSigner()
  const dispatch = useAppDispatch()
  const changePubKeyChainId = useChangePubKeyChainId()
  const referralCode = useAtomValue(referralCodeAtom)

  return useCallback(
    async function activateAccount() {
      try {
        if (!signer) {
          throw new Error('No web3 signer')
        }
        const {
          layerTwoChainId = 0,
          layerOneChainId = 0,
          mainContract = '',
        } = NetworkRegistry.byL2(changePubKeyChainId) ?? {}
        dispatch(updateAccountActivatingAction(true))
        const state = store.getState()
        const { currencies } = state.market.trades
        const supportCurrencies = currencies.map((v) => v.name)
        const { layer2Id = 0 } = state.account.accountInfo || {}
        const balance = state.account.balances.find((v) =>
          supportCurrencies.includes(v.currency),
        )

        if (!balance) {
          throw new Error('activateAccount: balance is undefined')
        }
        const token = currencies.find((v) =>
          Equal.upperCase(v.name, balance.currency),
        )

        if (!token || !token?.l2CurrencyId) {
          throw new Error('activateAccount: fee token is undefined')
        }
        if (!layerOneChainId) {
          throw new Error(
            `activateAccount: invalid layer one chain id, ${layerOneChainId}`,
          )
        }
        if (!mainContract) {
          throw new Error(
            `activateAccount: invalid contract address, ${mainContract}`,
          )
        }
        if (!layer2Id) {
          throw new Error(
            `activateAccount: invalid layer2 account id, ${layer2Id}`,
          )
        }

        if (!isValidReferralCode(referralCode)) {
          throw new Error('Invalid referral code')
        }

        const { nonce } = await getAccountNonce(0).then((r) => r.data)
        const { data: _fee } = await getFee({
          chainId: layerTwoChainId,
          op: 'ChangePubKey',
          address: state.account.accountInfo?.address as string,
          currency: token.name,
        })

        const signedData = await signChangePubKey(signer, {
          accountId: layer2Id,
          subAccountId: SUB_ACCOUNT_ID,
          chainId: layerTwoChainId,
          ethAuthType: 'EthECDSA',
          feeTokenId: token.l2CurrencyId,
          fee: e2w(_fee).toString(),
          nonce: nonce,
        }).catch((e) => {
          throw new Error(e?.message)
        })

        const { tx, layer1_signature } = signedData

        const pubKey = signer.getPubkey()
        const body: PostChangePubKeyBody = {
          currency: token.name,
          chainId: layerTwoChainId,
          newPkHash: tx.newPkHash,
          fee: w2e(tx.fee.toString()),
          feeToken: token.l2CurrencyId,
          nonce: tx.nonce,
          ts: tx.ts,
          pubKey: pubKey.startsWith('0x') ? pubKey.slice(2) : pubKey,
          signature: tx.signature.signature,
          ethAuthData: tx.ethAuthData,
          ethSignature: layer1_signature,
          inviteCode: referralCode,
        }

        await postChangePubKey(0, body)

        const isSigningKeySet = await waitActivateAccount()
        dispatch(updateAccountLockState({ isLocked: !isSigningKeySet }))
      } catch (e: any) {
        if (UserRejectedError.is(e)) {
        } else {
          console.error(e)
          toastify.error(e.message)
        }
      } finally {
        dispatch(updateAccountActivatingAction(false))
        dispatch(updateModal({ modal: 'verify', open: false }))
      }
    },
    [dispatch, signer, changePubKeyChainId, referralCode],
  )
}

export async function waitActivateAccount() {
  const state = store.getState()
  const { address = '' } = state.account.accountInfo ?? {}

  if (!address) {
    throw new Error('Missing address to requests account info')
  }
  while (
    (
      (
        await store.dispatch(
          getAccountInfoAction({
            address,
          }),
        )
      ).payload as AccountInfo
    ).status !== ActivateStatus.ACTIVATION_SUCCESS
  ) {
    await sleep(2000)
  }
  return true
}
